import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { SKILL_LEVEL_LABELS, formatGameDate, formatGameTime, formatCost } from "./GameCard";

const LEAGUE_TYPE_LABELS = {
  PICKUP: "Pickup",
  COMPETITIVE: "Competitive",
};

function spotsChipColor(open, max) {
  if (max === 0) return "default";
  if (open === 0) return "error";
  if (open / max <= 0.25) return "warning";
  return "success";
}

export default function LeagueCard({ league, onSignUp }) {
  const { name, city, state, skillLevel, leagueType, gamePaymentType, playerCost: rawPlayerCost, goalieCost: rawGoalieCost, maxSkaterSpots, maxGoalieSpots, upcomingGameCount, nextGame, rinks, superAdmin } = league ?? {};

  const cityState = city && state ? `${city}, ${state}` : city || state || null;
  const skillLabel = SKILL_LEVEL_LABELS[skillLevel] ?? skillLevel ?? "—";
  const playerCost = formatCost(rawPlayerCost);
  const goalieCost = formatCost(rawGoalieCost);
  const costDisplay = rawGoalieCost === 0 || rawGoalieCost == null
    ? `${playerCost} (Goalies free)`
    : `${playerCost} / Goalies: ${goalieCost}`;
  const costLabel = gamePaymentType === "FULL_LEAGUE" ? "Full league" : "Per game (drop-in)";
  const organizerName = superAdmin?.firstName && superAdmin?.lastName
    ? `${superAdmin.firstName} ${superAdmin.lastName}`
    : null;

  const openSkaterSpots = nextGame && maxSkaterSpots != null
    ? Math.max(0, maxSkaterSpots - (nextGame.skaterSpotsFilled ?? 0))
    : null;
  const openGoalieSpots = nextGame && maxGoalieSpots != null
    ? Math.max(0, maxGoalieSpots - (nextGame.goalieSpotsFilled ?? 0))
    : null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
              {name}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {skillLevel && <Chip label={skillLabel} size="small" variant="outlined" />}
              {leagueType && <Chip label={LEAGUE_TYPE_LABELS[leagueType] ?? leagueType} size="small" variant="outlined" />}
            </Stack>
          </Stack>

          {cityState && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <PlaceIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">{cityState}</Typography>
            </Stack>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary" display="block">Cost</Typography>
              <Typography variant="body2">{costDisplay}</Typography>
              <Typography variant="caption" color="text.secondary">{costLabel}</Typography>
            </Box>
            {maxSkaterSpots != null && (
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block">Roster per game</Typography>
                <Typography variant="body2">
                  {maxSkaterSpots} skaters{maxGoalieSpots != null ? ` · ${maxGoalieSpots} goalies` : ""}
                </Typography>
              </Box>
            )}
            {organizerName && (
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block">Organized by</Typography>
                <Typography variant="body2">{organizerName}</Typography>
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary" display="block">Upcoming games</Typography>
              <Typography variant="body2">
                {upcomingGameCount != null ? `${upcomingGameCount} game${upcomingGameCount !== 1 ? "s" : ""}` : "—"}
              </Typography>
            </Box>
          </Stack>

          {nextGame && (
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Next: {formatGameDate(nextGame.startsAt)} at {formatGameTime(nextGame.startsAt)}
                  {nextGame.durationMinutes ? ` · ${nextGame.durationMinutes} min` : ""}
                </Typography>
              </Stack>
              {(openSkaterSpots !== null || openGoalieSpots !== null) && (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <SportsHockeyIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Open spots for next game:</Typography>
                  {openSkaterSpots !== null && (
                    <Chip
                      size="small"
                      label={openSkaterSpots === 0 ? "Skaters full" : `${openSkaterSpots} skater${openSkaterSpots !== 1 ? "s" : ""} open`}
                      color={spotsChipColor(openSkaterSpots, maxSkaterSpots)}
                      variant="outlined"
                    />
                  )}
                  {openGoalieSpots !== null && (
                    <Chip
                      size="small"
                      label={openGoalieSpots === 0 ? "Goalies full" : `${openGoalieSpots} goalie${openGoalieSpots !== 1 ? "s" : ""} open`}
                      color={spotsChipColor(openGoalieSpots, maxGoalieSpots)}
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {Array.isArray(rinks) && rinks.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {rinks.length === 1 ? "Rink" : "Rinks"}
              </Typography>
              <Stack spacing={0.5}>
                {rinks.map((rink) => (
                  <Stack key={rink.id} direction="row" alignItems="center" spacing={1}>
                    <PlaceIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Typography variant="body2">
                      {rink.name}{rink.address ? ` — ${rink.address}` : ""}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {!nextGame && upcomingGameCount === 0 && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No upcoming games scheduled yet.
            </Typography>
          )}

          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            {onSignUp && (
              <Button variant="contained" color="success" onClick={() => onSignUp(league)}>
                View &amp; Sign Up
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
